import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, KanbanSquare, BellRing, UserCog, Wallet, History, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";
import { RoleBadge } from "./Badges";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "sales", "hr", "accounts"] },
  { to: "/leads", label: "Leads", icon: Users, roles: ["admin", "sales"] },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, roles: ["admin", "sales"] },
  { to: "/followups", label: "Follow-ups", icon: BellRing, roles: ["admin", "sales"] },
  { to: "/employees", label: "Employees", icon: UserCog, roles: ["admin", "hr"] },
  { to: "/accounts", label: "Accounts", icon: Wallet, roles: ["admin", "accounts"] },
  { to: "/activity", label: "Activity Log", icon: History, roles: ["admin", "sales", "hr", "accounts"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user.role));
  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-4 sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-2 py-3"><Logo /></div>
        <nav className="mt-6 flex-1 space-y-1">
          {items.map(({ to, label, icon: I }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
              <I className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name}</div>
          <div className="text-xs text-slate-500 truncate mb-2">{user.email}</div>
          <div className="flex items-center justify-between">
            <RoleBadge role={user.role} testid="current-user-role" />
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}
              className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-rose-600 transition-colors">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden flex items-center justify-between border-b bg-white px-4 py-3">
          <Logo small />
          <button data-testid="logout-btn-mobile" onClick={async () => { await logout(); nav("/login"); }} className="text-sm text-slate-600">Logout</button>
        </header>
        <div className="md:hidden flex gap-1 overflow-x-auto border-b bg-white px-2 py-2 scroll-thin">
          {items.map(({ to, label }) => (
            <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${isActive ? "bg-emerald-600 text-white" : "text-slate-600"}`}>{label}</NavLink>
          ))}
        </div>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 fade-up">{children}</main>
      </div>
    </div>
  );
}
